import {useEffect, useState, ReactNode} from 'react'
import {useRouter} from 'next/router'
import Link from 'next/link'
import {getCookie, setCookie} from 'cookies-next'
import _ from 'lodash'
import type {GetServerSidePropsContext, GetServerSidePropsResult} from 'next'
// Contexts
import {useBallotContext} from '../context/ballot'
// Layouts
import BlocksRenderer from '../components/layout/blocks-renderer'
import ModalContainer from '../components/layout/modal-container'
import PagePasswordInput from '../components/layout/page-password-input'
import PageSimplePage from '../components/layout/page-simple-page'
// Components
import ClientHead from '../components/client-head'
import ClientMeta from '../components/client-meta'
import ClientText from '../components/client-text'
import LoadingIndicator from '../components/loading-indicator'
import Navbar from '../components/navbar'
// Utils
import {accessBallotIsPublic, accessBallotIsPublished, accessBallotDisplayType} from '../utils/ballot-access'
import {
  calculateBallotPassword,
  calculateHasOnlyOneGroup,
  calculateSponsorshipAdPoolByBallot,
} from '../utils/ballot-calculators'
import {recordAdAndPageViews} from '../utils/analytics'
import {convertHoursToSeconds} from '../utils/number-helpers'
import {loadBallotByHost} from '../utils/prerender-helpers'
// Consts
import {backupBallot} from '../consts/ballots'
// Styles
import pageStyles from '../components/layout/page.module.scss'
// Types
import type {
  Ballot,
  BallotPageBlock,
  LandingPage,
} from '../types/ballot'
import type {Customer} from '../types/customer'

interface BallotShowProps {
  initBasicBallot: Ballot
  dataFetchingError?: {message: string} | null
}

const parseStoredBlocks = (storedBlocks: string | undefined): BallotPageBlock[] | null => {
  if (!storedBlocks) return null

  try {
    const parsedBlocks = JSON.parse(storedBlocks) as unknown

    if (!Array.isArray(parsedBlocks)) return null

    return _.filter(parsedBlocks, (block): block is BallotPageBlock => {
      return _.isPlainObject(block) && typeof block.blockType === 'string' && typeof block.blockLabel === 'string'
    })
  } catch {
    return null
  }
}

const calculateHasOnlyOneQuestionInOnlyGroup = (ballot: Ballot): boolean => {
  if (!calculateHasOnlyOneGroup(ballot)) return false

  const onlyGroup = _.first(ballot.parent_categories)
  if (!onlyGroup) return false

  const namedQuestions = _.filter(onlyGroup.sub_categories, question => Boolean(question?.name))
  return namedQuestions.length === 1
}

const BallotShow = ({
  initBasicBallot,
  dataFetchingError,
}: BallotShowProps) => {
  // console.log ('BallotShow initBasicBallot', initBasicBallot)
  if (dataFetchingError) return <PageSimplePage title={dataFetchingError.message} isErrorPage={true} />

  // Not yet loading fullBallot
  const {fullBallot, isLoadingFullBallot} = useBallotContext()

  const router = useRouter()
  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(true)

  const [ballotIsPublished, setBallotIsPublished] = useState<boolean>(false)
  const [ballotIsPublic, setBallotIsPublic] = useState<boolean>(false)
  const [clientHeaderHtml, setClientHeaderHtml] = useState<string | false | null>(null)

  const [currentWindowWidth, setCurrentWindowWidth] = useState<number>(480)
  const [sidebarIsOpen, setSidebarIsOpen] = useState<boolean>(false)
  const sendSidebarIsOpen = (value: boolean): void => setSidebarIsOpen(value)

  const [ballotPassword, setBallotPassword] = useState<string | null>(calculateBallotPassword(initBasicBallot) ?? null)
  const sendHasEnteredCorrectPassword = (isCorrect: boolean): void => {
    setCookie('_uhecp', String(isCorrect), {maxAge: convertHoursToSeconds(90), path: '/'})
    location.reload()
  }

  const [modalIsIn, setModalIsIn] = useState<boolean>(false)
  const [currentModal, setCurrentModal] = useState<ReactNode | null>(null)
  const [currentModalTitle, setCurrentModalTitle] = useState<string | null>(null)
  const [currentModalHasCloseButton, setCurrentModalHasCloseButton] = useState<boolean | null>(null)
  const onUpdateModal = (
    modalContents: ReactNode | null = null,
    modalTitle: string | null = null,
    modalHasCloseButton: boolean = false,
  ): void => {
    if (modalContents) {
      document.body.style.overflow = 'hidden'
      setCurrentModal(modalContents)
      setCurrentModalTitle(modalTitle)
      setCurrentModalHasCloseButton(modalHasCloseButton)
      setModalIsIn(true)
    } else {
      document.body.style.overflow = 'auto'
      setCurrentModal(null)
      setModalIsIn(false)
    }
  }

  const [blocks, setBlocks] = useState<BallotPageBlock[] | null>(null)

  const setAllPageState = (ballot: Ballot): void => {
    if (_.isEmpty(ballot)) return

    setCookie('ballot_id', ballot._id, {maxAge: convertHoursToSeconds(90), path: '/'})

    const newBallotIsPublished = accessBallotIsPublished(ballot)
    setBallotIsPublished(newBallotIsPublished)

    const newBallotIsPublic = accessBallotIsPublic(ballot) || (getCookie('_uhecp') === 'true')
    setBallotIsPublic(newBallotIsPublic)

    const newClientHeaderHtml = (ballot.header?.header_type === 'custom') && ballot.header?.html
    setClientHeaderHtml(newClientHeaderHtml ?? null)

    const newBallotPassword = calculateBallotPassword(ballot)
    setBallotPassword(newBallotPassword ?? null)

    const getStarterBlocks = (landingPage: LandingPage | null = null): BallotPageBlock[] => {
      const onlyGroup = _.first(ballot.parent_categories)

      // If there are no stored homepage blocks, display these.
      const starterTitleBlock: BallotPageBlock = {
        blockType: 'titleBlock',
        blockLabel: 'Starter title block',
        formValuesObject: {
          blockTitle: landingPage?.title || ballot?.ballot_name || null,
          blockSpaceAbove: 20,
        },
      }
      const starterImageBlock: BallotPageBlock = {
        blockType: 'imageBlock',
        blockLabel: 'Starter image block',
        formValuesObject: {
          blockImage: landingPage?.cover_image || null,
          blockSpaceAbove: 20,
        },
      }
      const starterRenderedTextBlock: BallotPageBlock = {
        blockType: 'renderedTextBlock',
        blockLabel: 'Starter custom text block',
        formValuesObject: {
          blockRenderedText: landingPage?.main_content || null,
          blockSpaceAbove: 20,
        },
      }
      const starterStartButtonBlock: BallotPageBlock = {
        blockType: 'startButtonBlock',
        blockLabel: 'Starter start button block',
        formValuesObject: {
          blockButtonText: 'Start',
          blockSpaceAbove: 20,
        },
      }

      if (!starterTitleBlock.formValuesObject.blockTitle) {
        starterTitleBlock.formValuesObject.blockSpaceAbove = 0
      }
      if (!starterImageBlock.formValuesObject.blockImage) {
        starterImageBlock.formValuesObject.blockSpaceAbove = 0
      }
      if (!starterRenderedTextBlock.formValuesObject.blockRenderedText) {
        starterRenderedTextBlock.formValuesObject.blockSpaceAbove = 0
      }
      if (!starterStartButtonBlock.formValuesObject.blockButtonText) {
        starterStartButtonBlock.formValuesObject.blockSpaceAbove = 0
      }

      const displayType = accessBallotDisplayType(ballot)
      const isNotLandingPageOnly = displayType !== 'landing-page-only'
      const ballotSetup = ballot?.ballot_setup?.toLowerCase?.()

      let listBlock: BallotPageBlock | null = null
      if (isNotLandingPageOnly && ballotSetup === 'groupsandcategories') {
        listBlock = {
          blockType: 'groupListBlock',
          blockLabel: 'Starter group list block',
          formValuesObject: {blockSpaceAbove: 20},
        }
      } else if (isNotLandingPageOnly && ballotSetup === 'multiple') {
        listBlock = {
          blockType: 'questionListBlock',
          blockLabel: 'Starter question list block',
          formValuesObject: {
            blockSelectedGroup: onlyGroup ? [onlyGroup] : undefined,
            blockSpaceAbove: 20,
          },
        }
      }

      const starterBlocks: BallotPageBlock[] = [
        starterTitleBlock,
        starterImageBlock,
        starterRenderedTextBlock,
        starterStartButtonBlock,
        ...(listBlock ? [listBlock] : []),
      ]

      return starterBlocks
    }
    const starterBlocks = getStarterBlocks(ballot.landing_page)
    const storedBlocks = parseStoredBlocks(ballot.additional_info?.ballot_page_building_blocks)
    let newBlocks = storedBlocks || starterBlocks

    // Filter blocks if ballot is not published
    if (!newBallotIsPublished) {
      newBlocks = _.filter(newBlocks, block => {
        return (
          block.blockType === 'titleBlock'
          || block.blockType === 'imageBlock'
          || block.blockType === 'renderedTextBlock'
          || block.blockType === 'sponsorBlock'
        )
      })
    } else {
      // Handle group/question list blocks for published ballots
      const onlyGroup = _.first(ballot.parent_categories)
      const newGroupListBlock: BallotPageBlock = {
        blockType: 'groupListBlock',
        blockLabel: 'Starter group list block',
        formValuesObject: {blockSpaceAbove: 20},
      }
      const newQuestionListBlock: BallotPageBlock = {
        blockType: 'questionListBlock',
        blockLabel: 'Starter question list block',
        formValuesObject: {
          blockSelectedGroup: onlyGroup ? [onlyGroup] : undefined,
          blockSpaceAbove: 20,
        },
      }
      const groupListBlockIndex = _.findIndex(newBlocks, block => block.blockType === 'groupListBlock')
      const questionListBlockIndex = _.findIndex(newBlocks, block => block.blockType === 'questionListBlock')

      const ballotHasOnlyOneGroup = calculateHasOnlyOneGroup(ballot)
      const ballotHasOnlyOneQuestionInOnlyGroup = calculateHasOnlyOneQuestionInOnlyGroup(ballot)

      if (ballotHasOnlyOneQuestionInOnlyGroup) {
        newBlocks = _.filter(newBlocks, block => {
          return block.blockType !== 'groupListBlock' && block.blockType !== 'questionListBlock'
        })
      } else if (ballotHasOnlyOneGroup) {
        if (questionListBlockIndex <= -1) {
          if (groupListBlockIndex > -1) {
            newBlocks.splice(groupListBlockIndex, 1, newQuestionListBlock)
          } else {
            newBlocks.push(newQuestionListBlock)
          }
        } else if (groupListBlockIndex > -1) {
          newBlocks.splice(groupListBlockIndex, 1)
        }
      } else if (groupListBlockIndex <= -1) {
        if (questionListBlockIndex > -1) {
          newBlocks.splice(questionListBlockIndex, 1, newGroupListBlock)
        } else {
          newBlocks.push(newGroupListBlock)
        }
      } else if (questionListBlockIndex > -1) {
        newBlocks.splice(questionListBlockIndex, 1)
      }
    }

    // TODO: Update if sponsorshipAds can rotate
    const keenSponsorshipAds = calculateSponsorshipAdPoolByBallot(ballot)

    // Add sponsorBlock if there are sponsorship ads and no sponsorBlock already exists
    const hasSponsorBlock = _.findIndex(newBlocks, block => block.blockType === 'sponsorBlock') > -1
    if (!hasSponsorBlock && keenSponsorshipAds && keenSponsorshipAds.length > 0) {
      const sponsorBlock: BallotPageBlock = {
        blockType: 'sponsorBlock',
        blockLabel: 'Sponsor ad block',
        formValuesObject: {blockSpaceAbove: 20},
      }
      const startButtonBlockIndex = _.findIndex(newBlocks, block => block.blockType === 'startButtonBlock')
      if (startButtonBlockIndex > -1) {
        newBlocks.splice(startButtonBlockIndex, 0, sponsorBlock)
      }
    }

    setBlocks(newBlocks)

    if (router.asPath && ballot._id) {
      const pageviewProps = {
        ballotId: ballot._id,
        sponsorshipAds: keenSponsorshipAds,
        componentName: 'BallotShow' as const,
        ballot: ballot,
      }
      recordAdAndPageViews(pageviewProps)
    }
    setIsLoadingPage(false)
  }

  const handleResize = (): void => {
    const newCurrentWindowWidth = window.innerWidth
    setCurrentWindowWidth(newCurrentWindowWidth)
  }
  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!isLoadingFullBallot && fullBallot) {
      setIsLoadingPage(true)
      setAllPageState(fullBallot)
    }
  }, [fullBallot])

  return <>
    <ClientMeta ballot={initBasicBallot || backupBallot} pageTitle={`${initBasicBallot?.ballot_name || backupBallot.ballot_name} - Home`} />
    {isLoadingPage && <LoadingIndicator />}
    {!isLoadingPage && _.isEmpty(fullBallot) && (
      <PageSimplePage title='The ballot you are looking for does not exist'>
        <h2 className='title is-4 pb-2'>
          Try returning to the home page and signing in again
        </h2>
        <Link
          href='/'
          className='button is-large is-dark'
        >
          OK
        </Link>
      </PageSimplePage>
    )}
    {!isLoadingPage && !_.isEmpty(fullBallot) && <>
      {ballotIsPublic && <>
        <ClientHead ballot={fullBallot} />
        <Navbar
          customer={(initBasicBallot.customer ?? null) as Customer | null}
          ballot={fullBallot}
          currentWindowWidth={currentWindowWidth}
          ballotIsPublished={ballotIsPublished}
          sidebarIsOpen={sidebarIsOpen}
          sendSidebarIsOpen={sendSidebarIsOpen}
          onUpdateModal={onUpdateModal}
        />
        <ClientText clientText={clientHeaderHtml || undefined} isClientHeaderHtml={true} />
        <div
          className={`
              ${pageStyles['page']}
              ${modalIsIn ? pageStyles['--modal-is-in'] : ''}
            `}
        >
          <BlocksRenderer
            ballot={fullBallot}
            blocks={blocks}
          />
        </div>
        {modalIsIn && currentModal &&
          <ModalContainer
            modalTitle={currentModalTitle ?? undefined}
            modalHasCloseButton={currentModalHasCloseButton ?? undefined}
            onUpdateModal={onUpdateModal}
          >
            {currentModal}
          </ModalContainer>
        }
      </>}
      {!ballotIsPublic &&
        <PagePasswordInput ballotPassword={ballotPassword ?? ''} sendHasEnteredCorrectPassword={sendHasEnteredCorrectPassword}/>
      }
    </>}
  </>
}

export default BallotShow

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<BallotShowProps>> => {
  const {initBasicBallot, prerenderError, customRedirect} = await loadBallotByHost(context) as any
  if (prerenderError) return prerenderError
  if (customRedirect) return customRedirect

  return {props: {initBasicBallot}}
}
